<script setup lang="ts">

import { ref, computed } from 'vue'

const props = defineProps(["isAIWriting", "projectMembers"])
const emits = defineEmits(['messagePosted'])
const hasTaggedMember = ref(false)
const newMessage = ref('')
const aiTextArea = ref(null)

const isPostingMessage = ref(false)

const placeholderText = computed(() => {
    if (props.projectMembers && props.projectMembers.length > 0) {
        return 'Mesajınıza "@" ile başlayarak proje üyelerini etiketleyebilirsiniz.';
    }
    return 'Mesajınızı buraya yazın...';
})

const filteredMembers = computed(() => {
    if (!newMessage.value.startsWith('@') || hasTaggedMember.value) return [];
    
    const searchText = newMessage.value.substring(1).toLowerCase();
    if (!searchText) return props.projectMembers || [];
    
    return (props.projectMembers || []).filter(member => 
        member.name.toLowerCase().startsWith(searchText)
    );
})

const onInput = (event) => {
    newMessage.value = event.target.innerText;
    // Reset hasTaggedMember if the message doesn't start with @
    if (!newMessage.value.startsWith('@')) {
        hasTaggedMember.value = false;
    }
}

const tagMember = (memberName: string) => {
    // Add the tagged member at the start of the message
    const newMessageContent = `@${memberName}`;
    newMessage.value = newMessageContent;
    
    // Update the contenteditable div
    if (aiTextArea.value) {
        aiTextArea.value.innerText = newMessageContent;
        
        // Move cursor to the end
        const range = document.createRange();
        const sel = window.getSelection();
        range.selectNodeContents(aiTextArea.value);
        range.collapse(false);
        sel?.removeAllRanges();
        sel?.addRange(range);
    }
    
    hasTaggedMember.value = true;
}

const postMessage = () => {
    emits('messagePosted', newMessage.value);
    newMessage.value = '';
    hasTaggedMember.value = false;
    if (aiTextArea.value) {
        aiTextArea.value.innerText = '';
    }
}

</script>

<template>
 <div class="textarea-container border-token-border-default flex w-full cursor-text flex-col items-center justify-center rounded-[28px] border bg-clip-padding contain-inline-size overflow-clip shadow-sm sm:shadow-lg dark:shadow-none! bg-token-main-surface-primary dark:bg-[#303030]">
    <div ref="aiTextArea" class="ai-textarea " :contenteditable=" !isPostingMessage" @input="onInput"
                @keydown.enter.prevent="postMessage" :placeholder="placeholderText"
                v-if="!props.isAIWriting">
            </div>
       
            <div class="ai-writing" v-else>
                <div class="loader"></div>
                <p>DansukAI size yanıt yazıyor... Tamamlandığında tüm yanıtı göreceksiniz.</p>
            </div>
            <div class="send-button-container">
                <button v-if="!props.isAIWriting" class="send-button" @click="postMessage">&#8593;</button>
            </div>
            <div v-if="filteredMembers.length > 0 && !hasTaggedMember" class="project-members-container">
                <ul>
                    <li @click="tagMember(member.name)" v-for="member in filteredMembers" :key="member.id">
                        {{ member.name }}
                    </li>
                </ul>
            </div>
        </div>
</template>

<style scoped>
.textarea-container {
    position: relative;
    top: 6%;
    max-width: 76.8rem;
    width: 76.8rem;
    background-color: #fff;
    border: 1px solid rgb(182, 182, 182);
    border-radius: 12px;
    z-index: 99;
    overflow: visible;
}

.ai-textarea::-webkit-scrollbar,
.ai-writing::-webkit-scrollbar {
    display: none;
}

.ai-writing,
.ai-textarea {
    border-radius: 12px;
    padding: 1.6rem 1.6rem 1.6rem 0;
    font-size: 1.6rem;
    resize: none;
    outline: none;
    font-weight: 400;
    line-height: 1.6;
    min-height: 40px;
    max-height: 25dvh;
    overflow-y: auto;
    color: black;
    scrollbar-width: none;
    -ms-overflow-style: none;
}


.ai-textarea {
    width: 90%;
    max-width: 90%;
    max-height: 25dvh;
}

.ai-textarea:empty:before {
    content: attr(placeholder);
    color: #afafaf;
    font-size: 1.6rem;
}

.ai-textarea.is-disabled {
    cursor: not-allowed;
    font-size: 1.4rem;

}

.send-button-container {
    position: absolute;
    top: 25%;
    right: 1rem;
    width: 5%;
    display: flex;
    justify-content: center;
}

.send-button {
    font-weight: 400;
    font-size: 1.6rem;
    width: 2.6rem;
    height: 2.6rem;
    border: none;
    border-radius: 50%;
    background-color: #000000;
    color: white;
    cursor: pointer;
    transition: all .2s;
}

.send-button:hover {
    transform: scale(1.1);
    background-color: rgb(57, 129, 115);
}

.send-button:disabled {
    background-color: #808080;
    cursor: not-allowed;
}

.send-button:active {
    transform: scale(.8);
}

.ai-writing {
    display: flex;
    gap: 1.6rem;
    align-items: center;
    font-size: 1.4rem;
    color: #aeaeae;
}

.loader {
  width: 20px;
  aspect-ratio: 2;
  --_g: no-repeat radial-gradient(circle closest-side,#c4c4c4 90%,#0000);
  background: 
    var(--_g) 0%   50%,
    var(--_g) 50%  50%,
    var(--_g) 100% 50%;
  background-size: calc(100%/3) 50%;
  animation: l3 1s infinite linear;
}
@keyframes l3 {
    20%{background-position:0%   0%, 50%  50%,100%  50%}
    40%{background-position:0% 100%, 50%   0%,100%  50%}
    60%{background-position:0%  50%, 50% 100%,100%   0%}
    80%{background-position:0%  50%, 50%  50%,100% 100%}
}

.project-members-container {
    position: absolute;
    top: 0;
    left: 0;
    width: fit-content;
    height: fit-content;
    overflow-y: auto;
    display: flex;
    justify-content: center;
    z-index: 100;
    pointer-events: auto;
    transform: translateY(-100%);
    margin-top: -1.6rem; /* Match the padding of the textarea */
}

.project-members-container ul {
    list-style: none;
    padding: 0;
    margin: 0;
    background-color: #fff;
    border: 1px solid rgb(182, 182, 182);
    border-radius: 12px;
    min-width: 200px; /* Ensure minimum width for better appearance */
}

.project-members-container li {
    padding: 0;
    margin: 0;
    padding: 1.6rem;
    border-bottom: 1px solid rgb(182, 182, 182);
    cursor: pointer;
}

.project-members-container li:last-child {
    border-bottom: none;
}

.project-members-container li:hover {
    background-color: #f0f0f0;
}

@media screen and (max-width: 950px){
    .textarea-container {
        margin-right: auto;
        margin-left: auto;
        width: 100%;
        max-width: 100%;
        margin-top: 1.6rem;
    }

    .send-button-container {
        width: 4rem;
        top: 30%;
        right: 0;
    }

    .send-button {
        font-size: 1.6rem;
    }
}
</style>